import { NavLink } from 'react-router-dom';
import { useAppSelector } from '@store';
import {
  BlacklistWarningItem,
  ButtonPseudo,
  ButtonSecondary,
  ButtonSize,
  IconChangeLoClock,
  PermissionEnum,
  Popup,
} from '@ui/src';
import { OngoingMonitoringPanelTypes } from '../types';
import useOngoingMonitoringPanel from '../hooks/use-ongoing-monitoring-panel';
import { CustomerStatus } from '../../types';
import OngoingMonitroingPanelPopup from '../../../../components/common/ongoing-monitoring/panel-popup-content';

import cn from '../styles.module.css';

const OngoingMonitoringPanel = ({
  warnings,
  blacklists,
  setOngoingItemPanelVisible,
  setBlacklistDataVisible,
  setBlacklistData,
  setBlacklistDataItem,
  setBlacklistItemVisible,
  type,
  applyError,
  applyWarningInProgress,
  applyOngoingWarning,
  warningGroupId,
  batchApplyWarningInProgress,
  batchApplyOngoingWarning,
  batchApplyWarningIsSuccess,
  onResetTransactionWarningAction,
  resetTransactionsWarningIsLoading,
  resetTransactionsWarningIsSuccess,
  selectedWarningId,
  status,
  warningsPagination,
  getWarningsAction,
  paginationInProgress,
  selectedWarningGroup,
  getOngoingWarningById,
  isWarningByIdLoading,
  isWarningByIdFetching,
}: OngoingMonitoringPanelTypes) => {
  const { activePermissions } = useAppSelector((state) => state.root);
  const { currentEmployee } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const {
    applyAllPopupVisible,
    setApplyAllPopupVisible,
    matchStatus,
    setMatchStatus,
    action,
    setAction,
    comment,
    setComment,
    aproveAllowed,
    rejectAllowed,
    onApplyActions,
  } = useOngoingMonitoringPanel({
    batchApplyOngoingWarning,
    warningGroupId,
    batchApplyWarningIsSuccess,
    type,
    activePermissions,
  });

  const batchApplyDisabled = !warnings?.filter((item) => !item.action)?.length;

  return (
    <div className={cn.ongoingPannelContent}>
      <div className={cn.ongoinActionsTitle}>
        <div>Found matches that require a solution</div>
        {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
          <NavLink
            to={`/administration/change-logs?entityType=WARNING_GROUP&entityId=${warningGroupId}`}
            className={cn.applicationLink}
            target="_blank"
          >
            <IconChangeLoClock />
            Change Log
          </NavLink>
        )}
      </div>
      {(aproveAllowed || rejectAllowed) &&
        !batchApplyDisabled &&
        status !== CustomerStatus.CLOSED && (
          <ButtonSecondary
            size={ButtonSize.SMALL}
            text="Apply to all matches"
            className={`button-white ${cn.applyAll}`}
            onClick={() => setApplyAllPopupVisible(true)}
          />
        )}
      <div className={cn.matchesWrapper}>
        {warnings?.map((item) => (
          <BlacklistWarningItem
            key={item.id}
            warning={item}
            employee={currentEmployee}
            onApplyTransactionAction={(data) => {
              applyOngoingWarning({
                id: warningGroupId,
                warningId: data.warningId,
                action: data.body.action,
                comment: data.body.comment,
                matchStatus: data.body.matchStatus,
              });
            }}
            setBlacklistDataVisible={() => {
              setOngoingItemPanelVisible(false);
              setBlacklistDataVisible(true);
            }}
            setBlacklistDataItem={(data) => setBlacklistDataItem(data)}
            setBlacklistItemVisible={() => {
              setOngoingItemPanelVisible(false);
              setBlacklistItemVisible(true);
            }}
            setBlacklistData={(data) => setBlacklistData(data)}
            activePermissions={activePermissions}
            hideApproveActions={!aproveAllowed}
            hideRejectActions={!rejectAllowed}
            applyTransactionsWarningIsLoading={applyWarningInProgress}
            onResetTransactionWarningAction={(id) => {
              if (onResetTransactionWarningAction) {
                onResetTransactionWarningAction(id, warningGroupId);
              }
            }}
            resetTransactionsWarningIsLoading={
              resetTransactionsWarningIsLoading
            }
            resetTransactionsWarningIsSuccess={
              resetTransactionsWarningIsSuccess
            }
            warningInGroupId={selectedWarningId}
            withoutActions={status === CustomerStatus.CLOSED}
            getWarningById={getOngoingWarningById}
            entityId={warningGroupId}
            isWarningByIdFetching={isWarningByIdFetching}
            isWarningByIdLoading={isWarningByIdLoading}
            globalCollapsed
            isPaymentOrder
            withoutChat
          />
        ))}
        {getWarningsAction && warningsPagination?.hasNextPage && (
          <div className={cn.warningsGroupPagination}>
            <ButtonPseudo
              text={
                selectedWarningGroup === warningGroupId && paginationInProgress
                  ? 'Loading...'
                  : 'Show more'
              }
              disabled={paginationInProgress}
              onClick={() =>
                getWarningsAction({
                  warningGroupId,
                  cursor: warningsPagination?.endCursor,
                })
              }
              bold
            />
          </div>
        )}
        {applyError && <div className={cn.applyError}>{applyError}</div>}
      </div>
      <div className={cn.ongoinActionsTitle}>Checked blocklists</div>
      <ul className={cn.blacklists}>
        {blacklists?.map((item) => (
          <li key={item.split(' ').join('')}>{item}</li>
        ))}
      </ul>
      <Popup
        title="Select status and action"
        description="This will be applied to all matches in the group"
        onCancelClick={() => {
          setApplyAllPopupVisible(false);
          setComment('');
          setMatchStatus('');
          setAction('');
        }}
        onOkClick={onApplyActions}
        cancelButtonText="Cancel"
        okButtonText={
          batchApplyWarningInProgress ? 'Loading...' : 'Apply to all'
        }
        okButtonDisabled={
          !comment || !action || !matchStatus || batchApplyWarningInProgress
        }
        isVisible={applyAllPopupVisible}
        parent={document.getElementById('apply-all-popup') as HTMLElement}
      >
        <OngoingMonitroingPanelPopup
          matchStatus={matchStatus}
          setMatchStatus={setMatchStatus}
          batchApplyDisabled={batchApplyDisabled}
          batchApplyWarningInProgress={batchApplyWarningInProgress}
          action={action}
          setAction={setAction}
          aproveAllowed={aproveAllowed}
          rejectAllowed={rejectAllowed}
          comment={comment}
          setComment={setComment}
        />
      </Popup>
    </div>
  );
};

export default OngoingMonitoringPanel;
